import type { Activity, Stage, TelegramChat } from '@/types/api';

/**
 * Lead stages in order (backend/pkg/api/stage.go; Q4, answered by Matt). Clarifications
 * is set by a person; the rest come from the platform (CAS-4). Blocked is a tag on a lead
 * in Configuring or Clarifications, not a stage (BLOCKED).
 */
export const STAGES: { value: Stage; label: string; hint: string }[] = [
  { value: 'new', label: 'New', hint: 'Has an account or wrote to us; nothing started yet' },
  { value: 'clarifications', label: 'Clarifications', hint: 'Asking questions before they start; the team is answering' },
  { value: 'configuring', label: 'Configuring', hint: 'Started building a casino in the wizard' },
  { value: 'paid', label: 'Paid', hint: 'A payment succeeded; the casino is not live yet' },
  { value: 'live', label: 'Live', hint: 'A casino is live: this lead is a sale' },
];

/** The stages a person sets on a lead; they end when the platform moves the lead on. */
export const MANUAL_STAGES: Stage[] = ['clarifications'];

/** The blocked tag, and the stages that can carry it; leaving them takes it off. */
export const BLOCKED = { label: 'Blocked', hint: 'Stuck: something stops them. A tag on Configuring or Clarifications' };
export const BLOCKABLE: Stage[] = ['configuring', 'clarifications'];

export const stageLabel = (s: string) => STAGES.find((x) => x.value === s)?.label ?? s;

/** What kind of Telegram chat it is, in a few words (Assistant's: ASSIST-1). */
export const chatKindLabel = (kind: TelegramChat['kind']) =>
  ({ group: 'Telegram group', private: 'Direct message to the bot', assistant: 'Chat with Assistant', assistant_group: 'Group with Assistant' })[kind];

const SOURCES: Record<string, string> = {
  google_ads: 'Google Ads',
  youtube_ads: 'YouTube Ads',
  meta_ads: 'Meta Ads',
  tiktok_ads: 'TikTok Ads',
  bing_ads: 'Bing Ads',
  reddit_ads: 'Reddit Ads',
  chatgpt: 'ChatGPT',
  perplexity: 'Perplexity',
  claude: 'Claude',
  gemini: 'Gemini',
  copilot: 'Copilot',
  referral: 'Referral',
  event: 'Event',
  unknown: 'Unknown',
  '': 'Unknown',
};

/** A first-touch source for display; raw utm_source values pass through. */
export const sourceLabel = (s: string) => SOURCES[s] ?? s;

const CHANNELS: Record<string, string> = {
  registration: 'Registration',
  contact_form: 'Contact form',
  telegram: 'Telegram',
  manual: 'Added by the team',
  email: 'Email',
};
export const channelLabel = (c: string) => CHANNELS[c] ?? c;

export const ACTIVITY: Record<Activity, { label: string; hint: string }> = {
  high_velocity: { label: 'High velocity', hint: 'Velocity 60 or more (SEG-1, Q16)' },
  active: { label: 'Active', hint: 'Live, between the dormant and high-velocity thresholds' },
  dormant: { label: 'Dormant', hint: 'Live, but velocity below 20 or no players (SEG-1, Q16)' },
  not_live: { label: 'Deploying', hint: 'Paid; the casino is not live yet' },
};

const CASINO_STATUS: Record<string, string> = {
  ok: 'Live',
  content_deployment_in_progress: 'Deploying',
  paid: 'Paid',
};
export const casinoStatusLabel = (s: string) => CASINO_STATUS[s] ?? (s || 'Unknown');
